import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { api, type StoredPlan } from "../../lib/api.js";

/**
 * Resolve with AI — propose, review, approve.
 *
 * The distinction this panel exists to preserve: the model does not fix
 * anything. It chooses from a closed set of moves the code can perform,
 * validate and reverse, and every choice arrives here with its reasoning and
 * its confidence attached. You approve what you want; the server re-validates
 * each step against live rows at that moment and reports what it refused.
 *
 * That refusal path is not theoretical. On the first live run the model
 * proposed 148 sibling links and the deterministic guards rejected 23 of them —
 * guided imagery folded into meditation, aromatherapy into facial massage,
 * acupuncture into a headache article. All plausible as text, all wrong as SEO.
 * A plan you cannot inspect would have shipped every one of them.
 *
 * Low-confidence steps are NOT pre-selected. If the model is choosing between
 * two defensible moves, that is exactly the case that needs your eye.
 */

const CONFIDENCE_STYLE: Record<string, string> = {
  high: "bg-accent-soft text-emerald-700",
  medium: "bg-warn-soft text-amber-800",
  low: "bg-danger-soft text-red-700",
};

export function ResolvePanel({ onApplied, onError }: { onApplied: () => void; onError: (m: string) => void }) {
  const qc = useQueryClient();
  /**
   * The plan lives on the SERVER, not here.
   *
   * It used to be React state, so leaving the page destroyed a proposal that
   * cost ~$0.22 and a minute of waiting — and there was no way back except to
   * buy it again. It is now persisted and reloaded on every visit, along with
   * which steps are ticked, so a half-finished review of 19 steps survives a
   * navigation.
   *
   * It also means a pending plan HOLDS PRODUCTION for the keywords it names:
   * `selectWork` skips them until you approve or discard. Otherwise the
   * autopilot could write the article before you decided what it should target,
   * and the approval would be paperwork.
   */
  const planQuery = useQuery({
    queryKey: ["preflight-plan"],
    queryFn: () => api.getPendingPlan(),
    // Poll WHILE the server is computing. This is the half that was missing:
    // the plan always survived navigation because it is persisted, but the
    // knowledge that one was on its way did not — leave mid-call, come back,
    // and the panel was empty while the server worked. Now any tab, any
    // reload, any return sees the same "still thinking, Ns elapsed".
    refetchInterval: (q) => (q.state.data?.inFlight ? 3000 : false),
    refetchOnWindowFocus: true,
  });
  const plan: StoredPlan | null = planQuery.data?.plan ?? null;
  const serverInFlight = planQuery.data?.inFlight ?? null;

  const [elapsed, setElapsed] = useState(0);
  // Prefer the SERVER's clock — it knows when the work actually started, even
  // if this browser only just arrived.


  const propose = useMutation({
    mutationFn: () => api.proposeResolutions(),
    onMutate: () => setElapsed(0),
    onSuccess: (r) => {
      qc.setQueryData(["preflight-plan"], r);
      const actionable = (r.plan?.fixes ?? []).filter((f) => f.action !== "no_action");
      if (!actionable.length) onError("The model found no safely automatable fix — every open finding is yours to judge.");
    },
    onError: (e: Error) => onError(e.message),
  });

  const toggleFix = useMutation({
    mutationFn: ({ fixId, approved }: { fixId: number; approved: boolean }) => api.approvePlanFix(fixId, approved),
    onSuccess: (r) => qc.setQueryData(["preflight-plan"], r),
    onError: (e: Error) => onError(e.message),
  });

  const apply = useMutation({
    mutationFn: () => api.applyPlan(plan!.id),
    onSuccess: (r) => {
      const okCount = r.results.filter((x) => x.ok).length;
      /**
       * Summarise refusals by REASON, not one line each.
       *
       * A real apply refused nine siblings and rendered every one in full, which
       * became a paragraph nobody could read — and the information in it was
       * three facts repeated nine times. The reasons are what the operator needs
       * ("six were already secondaries elsewhere"); the individual keywords are
       * on the rows themselves.
       */
      const refusals = r.results.filter((x) => !x.ok);
      const byReason = new Map<string, number>();
      for (const x of refusals) {
        for (const m of x.detail.matchAll(/\(([^)]+)\)/g)) {
          const reason = m[1]!;
          byReason.set(reason, (byReason.get(reason) ?? 0) + 1);
        }
      }
      const summary = [...byReason.entries()]
        .sort((a, b) => b[1] - a[1])
        .map(([reason, n]) => `${n} ${reason}`)
        .join(" · ");
      onError(
        `Applied ${okCount} of ${r.results.length}.` +
          (summary ? ` Guards refused: ${summary}.` : "") +
          " Findings marked YOUR CALL stay — only you can close those.",
      );
      void qc.invalidateQueries({ queryKey: ["preflight-plan"] });
      onApplied();
    },
    onError: (e: Error) => onError(e.message),
  });

  const discard = useMutation({
    mutationFn: () => api.discardPlan(plan!.id),
    onSuccess: () => {
      onError("Plan discarded — those keywords are released back into the queue.");
      void qc.invalidateQueries({ queryKey: ["preflight-plan"] });
      onApplied();
    },
    onError: (e: Error) => onError(e.message),
  });

  useEffect(() => {
    if (!propose.isPending) return;
    const t = window.setInterval(() => setElapsed((n) => n + 1), 1000);
    return () => window.clearInterval(t);
  }, [propose.isPending]);

  // Prefer the SERVER's clock — it knows when the work actually started, even
  // if this browser only just arrived at the page.
  const working = propose.isPending || Boolean(serverInFlight);
  const shownElapsed = serverInFlight ? Math.round(serverInFlight.elapsedMs / 1000) : elapsed;

  const fixes = plan?.fixes ?? [];
  const approvedCount = fixes.filter((f) => f.approved && f.action !== "no_action").length;
  const actionable = fixes.filter((f) => f.action !== "no_action");

  return (
    /*
      DELIBERATELY NOT STICKY — twice now.

      Sticking the whole panel fixed "I can't find the approve button" and broke
      scrolling outright: with a plan open the panel is ~3,200px against a
      ~970px viewport, and a sticky box taller than the viewport pins its top
      and hangs the rest over everything below. The page read as frozen.

      Sticking only the header was no better in the end: `position: sticky` is
      bounded by its own parent, so once the panel scrolls past the header
      leaves with it. All it bought was the layout risk.

      The panel is the FIRST thing on this page and the pre-flight section sits
      directly beneath it, so landing on /tasks always shows it. What made it
      feel missing was a plan list long enough to become the page — fixed by
      bounding the list, not by pinning the container.
    */
    <div className="rounded-xl border border-brand/30 bg-white shadow-sm">
      <div className="flex flex-wrap items-center gap-2 rounded-t-xl border-b border-line bg-white px-4 py-3">
        <Sparkles className="h-4 w-4 text-brand" />
        <span className="text-sm font-medium text-ink">Resolve with AI</span>
        <span className="text-xs text-muted">
          Proposes which known fix closes each finding. Nothing is written until you approve it.
        </span>
        <button
          type="button"
          disabled={working || apply.isPending}
          onClick={() => propose.mutate()}
          className="btn-secondary btn-sm ml-auto"
        >
          {working ? `Thinking… ${shownElapsed}s` : plan ? "Re-propose" : "Propose fixes"}
        </button>
      </div>

      <div className="px-4 pb-4 pt-3">
      {working && (
        <div className="mb-3 rounded border border-brand/30 bg-brand-50/50 px-3 py-2">
          <div className="flex items-center justify-between text-xs font-medium text-brand-700">
            <span>
              {serverInFlight?.keyword
                ? `Choosing a fix for “${serverInFlight.keyword}”…`
                : "Reading the queue and choosing a fix for each finding…"}
            </span>
            <span>{shownElapsed}s</span>
          </div>
          <div className="mt-1.5 h-1 overflow-hidden rounded bg-brand-100">
            {/* Indeterminate but honest: scaled against the ~60s typical case,
                capped so it never claims to be finished. */}
            <div
              className="h-full bg-brand transition-all duration-1000"
              style={{ width: `${Math.min(95, (shownElapsed / 60) * 100)}%` }}
            />
          </div>
          <p className="mt-1 text-2xs text-muted">
            Usually 50-95 seconds — one model call per batch of 5 candidates. This keeps running if you leave the
            page; come back any time and it will still be here.
          </p>
        </div>
      )}
      <p className="text-xs text-muted">
        Safety findings are never sent to the model — sensitive subjects, do-not-autopilot verdicts and safety notes
        stay yours. The model picks from a closed set of moves; the server re-validates every one against live rows
        before it writes.
      </p>

      {plan && (
        <div className="mt-2 text-xs text-muted">
          {plan.model} · ${(plan.costUsd ?? 0).toFixed(4)} · {plan.excluded} finding(s) withheld as yours ·{" "}
          proposed {new Date(plan.createdAt).toLocaleString()}
          {plan.rejected.length > 0 && (
            <span className="text-amber-700"> · {plan.rejected.length} proposal(s) failed validation and were dropped</span>
          )}
          <div className="mt-1 rounded border border-warn/30 bg-warn-soft px-2 py-1 text-amber-900">
            {approvedCount} approved step(s) are HOLDING PRODUCTION for their keywords — the autopilot will not write
            them until you apply or discard this plan.
          </div>
        </div>
      )}

      {plan && (
        <>
          {/* Bounded, with its own scroll — a 36-row plan must not become the page. */}
          <div className="mt-3 max-h-[55vh] space-y-1.5 overflow-y-auto pr-1">
            {fixes.map((f, i) =>
              f.action === "no_action" ? (
                <div key={i} className="rounded border border-line bg-line-soft/40 px-2.5 py-1.5 text-xs text-muted">
                  <span className="font-medium text-muted-dark" dir="auto">{f.keyword}</span> — needs you:{" "}
                  <span dir="auto">{f.reason}</span>
                </div>
              ) : (
                <label
                  key={i}
                  className={`flex cursor-pointer items-start gap-2.5 rounded border px-2.5 py-2 text-xs ${
                    f.approved ? "border-brand/40 bg-brand-50/40" : "border-line bg-white"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={f.approved}
                    disabled={toggleFix.isPending}
                    onChange={() => toggleFix.mutate({ fixId: f.fixId, approved: !f.approved })}
                    className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded border-line text-brand focus:ring-brand"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-1.5">
                      <span className="font-medium text-ink" dir="auto">{f.preview}</span>
                      <span className={`rounded px-1 py-px text-[10px] uppercase ${CONFIDENCE_STYLE[f.confidence]}`}>
                        {f.confidence}
                      </span>
                    </span>
                    <span className="mt-0.5 block text-muted" dir="auto">{f.reason}</span>
                  </span>
                </label>
              ),
            )}
          </div>

          {actionable.length > 0 && (
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                disabled={apply.isPending || approvedCount === 0}
                onClick={() => apply.mutate()}
                className="btn-primary btn-sm"
              >
                {apply.isPending ? "Applying…" : `Apply ${approvedCount} approved`}
              </button>
              <button
                type="button"
                disabled={discard.isPending}
                onClick={() => discard.mutate()}
                className="btn-ghost btn-sm text-muted"
              >
                {discard.isPending ? "Discarding…" : "Discard plan"}
              </button>
              <span className="text-xs text-muted">
                {actionable.length - approvedCount > 0 && `${actionable.length - approvedCount} left unchecked`}
              </span>
            </div>
          )}
        </>
      )}
      </div>
    </div>
  );
}
